import Vue from "vue";
import Swal from "sweetalert2";
import deepEquals from "fast-deep-equal";
import {isString} from "@/utils/TypeUtil";
import {formatError} from "@/utils/Formatter";

/**
 * 入出力が発生するページのコンポーネント型
 */
interface IoableMethod {
    /**
     * データの読込を行います
     */
    doLoad: () => Promise<void>;

    /**
     * 実際にデータの読込を行います。
     * 各ページで実装してください。
     */
    load: () => Promise<void>;

    /**
     * データの保存を行います
     */
    doSave: () => Promise<void>;

    /**
     * 実際にデータの保存を行います。
     * 各ページで実装してください。
     */
    save: () => Promise<void>;

    /**
     * 更新可能フラグを立てます
     */
    markDirty: () => void;
}

/** IoableRegistry を注入するための Symbol */
const ioableRegistry = Symbol('NestedIoable');

/** ネストした Ioable を登録するレジストリ */
class IoableRegistry {
    /** 親 */
    private parent: IoableMethod;

    /** すべての子 */
    private children = [] as IoableMethod[];

    /** 状態 */
    private state: 'unloaded' | 'loading' | 'loaded' = 'unloaded';

    /**
     * コンストラクタ
     *
     * @param parent 親
     */
    constructor(parent: IoableMethod) {
        this.parent = parent;
    }

    /**
     * 子を登録します
     *
     * @param ioable 子のインスタンス
     */
    register(ioable: IoableMethod, vm: Vue) {
        const index = this.children.indexOf(ioable);
        if(index === -1) {
            this.children.push(ioable);
            if(this.state === 'loaded') {
                const loading = vm.$loading({
                    lock: true,
                    text: '読み込み中...',
                    spinner: 'el-icon-loading',
                });
                Vue.nextTick()
                    .then(_ => {
                        return ioable.load();
                    }).catch(reason => {
                        vm.$store.commit({
                            type: 'showSnackbar',
                            color: 'failed',
                            message: `読込に失敗しました`
                        });
                        console.error(reason);
                    })
                    .finally(() => {
                        loading.close();
                    });
            }
        }
    }

    /**
     * 子の登録を解除します
     *
     * @param ioable 子のインスタンス
     */
    unregister(ioable: IoableMethod) {
        const index = this.children.indexOf(ioable);
        if(index >= 0) {
            this.children.splice(index, 1);
        }
    }

    /**
     * 子をすべてロードします
     */
    async load() {
        try {
            this.state = 'loading';
            for (const child of this.children) {
                await child.load();
            }
            this.state = 'loaded';
        } catch (e) {
            this.state = 'unloaded';
            throw e;
        }
    }

    /**
     * 子をすべてセーブします
     */
    async save() {
        for(const child of this.children) {
            await child.save();
        }
    }

    /**
     * 親の更新可能フラグを立てます
     */
    markDirty() {
        this.parent.markDirty();
    }
}

/**
 * データ型
 */
interface IoableData {
    /**
     * 更新可能フラグ
     */
    dirty: boolean;

    /**
     * この Ioable レジストリ
     */
    IoablePageMixin_private_selfRegistry: IoableRegistry;

    /**
     * 親の Ioable レジストリ
     */
    IoablePageMixin_private_parentRegistry?: IoableRegistry;
}


/**
 * IoablePageMixin
 */
const IoablePageMixin = Vue.extend<IoableData, IoableMethod, object, object>({
    provide() {
        return {
            [ioableRegistry]: this.IoablePageMixin_private_selfRegistry
        }
    },
    inject: {
        'IoablePageMixin_private_parentRegistry': {
            from: ioableRegistry,
            default: null
        }
    },
    data() {
        return {
            dirty: false,
            'IoablePageMixin_private_selfRegistry': new IoableRegistry(this as unknown as IoableMethod)
        };
    },
    created() {
        this.IoablePageMixin_private_parentRegistry && this.IoablePageMixin_private_parentRegistry.register(this, this);
    },
    beforeDestroy() {
        this.IoablePageMixin_private_parentRegistry && this.IoablePageMixin_private_parentRegistry.unregister(this)
    },
    beforeRouteEnter(to, from, next) {
        next(vm => {
            const self = vm as IoableMethod & IoableData & Vue;
            if(self.IoablePageMixin_private_parentRegistry) {
                return;
            }
            self.$store.dispatch('hcs/fetchLoginAccount')
                .then(() => {
                    return self.doLoad();
                }, () => {
                    return;
                });

        });
    },
    beforeRouteUpdate(to, from, next) {
        next();
        if(!this.IoablePageMixin_private_parentRegistry) {
            this.$nextTick(() => {
                this.doLoad();
            });
        }
    },
    beforeRouteLeave(to, from, next) {
        if (this.dirty === undefined || !this.dirty) {
            next();
            return;
        }
        if(this.IoablePageMixin_private_parentRegistry) {
            next();
            return;
        }

        Swal.fire({
            title: '確定内容の保存の確認',
            icon: 'info',
            html: "未保存の変更があります。<br>ページを離れる前に確定内容の保存を行いますか？",
            showCloseButton: true,
            showCancelButton: true,
            focusConfirm: true,
            confirmButtonText: "はい",
            cancelButtonText: "いいえ"
        })
            .then(result => {
                if (result.value) {
                    return 'save';
                } else if (result.dismiss === Swal.DismissReason.cancel) {
                    return 'discard';
                } else {
                    return 'cancel';
                }
            })
            .then(result => {
                switch (result) {
                    case 'cancel':
                        return Promise.reject();
                    case 'discard':
                        return Promise.resolve();
                    case 'save':
                        return this.doSave();
                }
            })
            .then(() => {
                next();
            }, () => {
                // cancel
            });
    },
    methods: {
        /**
         * 更新可能フラグを立てます
         */
        markDirty() {
            this.dirty = true;
            this.IoablePageMixin_private_parentRegistry && this.IoablePageMixin_private_parentRegistry.markDirty();
        },

        /**
         * データの読み込みを行います
         */
        doLoad() {
            const loading = this.$loading({
                lock: true,
                text: '読み込み中...',
                spinner: 'el-icon-loading',
            });
            return this.load()
                .then(() => {
                    return this.IoablePageMixin_private_selfRegistry.load();
                })
                .catch(reason => {
                    this.$store.commit({
                        type: 'showSnackbar',
                        color: 'failed',
                        message: `読込に失敗しました`
                    });
                    console.error(reason);
                })
                .finally(() => {
                    loading.close();
                    this.dirty = false;
                });
        },

        /**
         * データの保存を行います
         */
        doSave() {
            const loading = this.$loading({
                lock: true,
                text: '確定内容を保存しています...',
                spinner: 'el-icon-loading',
            });
            return this.save()
                .then(() => {
                    return this.IoablePageMixin_private_selfRegistry.save();
                })
                .then(() => {
                    this.dirty = false;
                    this.$store.commit({
                        type: 'showSnackbar',
                        message: `確定内容の保存に成功しました`
                    });
                    return this.load();
                })
                .then(() => {
                    this.dirty = false;
                })
                .catch(e => {
                    this.$store.commit({
                        type: 'showSnackbar',
                        color: 'failed',
                        message: formatError(e, '確定内容の保存に失敗しました')
                    });
                    console.error(e);
                })
                .finally(() => {
                    loading.close();
                });
        },

        load() {
            return Promise.resolve();
        },

        save() {
            return Promise.resolve();
        }
    }
});

/**
 * 子ページの切り替わりで再読込しない IoablePageMixin
 */
export const IoableParentPageMixin = IoablePageMixin.extend({
    beforeRouteUpdate(to, from, next) {
        if(to.hash !== from.hash ||
            !deepEquals(to.query, from.query) ||
            !deepEquals(to.params, from.params)) {
            this.doLoad();
        }
        next();
    },
});

export default IoablePageMixin;
